import { Location } from '@angular/common';
import {
  Component,
  DOCUMENT,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Observable, Subject, merge, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  SetActivePointId,
  SetActiveWorkspaceId,
} from 'src/app/project/services/active/active.actions';
import { AddPoint, DeletePoint } from '../points.actions';

import { TUser } from 'src/app/project/modules/user/user.model';
import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';
import { TSync } from '../../sync/sync.model';
import { TPoint, TPointsByWorkspace } from '../points.model';

import { GET_ACTIVE_PLAN } from 'src/app/project/modules/plan/plan.store';
import { GET_TIMELINE } from 'src/app/project/modules/site/site-timeline/timeline.ui.store';

import { ClosePointModalComponent } from '../close-point-modal/close-point-modal.component';
import { UnsavedFilesModalComponent } from '../unsaved-files-modal/unsaved-files-modal.component';

import { DeviceService, ScreenService } from '@core/services';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { ResponseErrorService } from 'src/app/project/modules/errors/response-error.service';
import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PlanPointService } from 'src/app/project/modules/plan/plan-point/plan-point.service';
import { SyncService } from 'src/app/project/modules/sync/sync.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { PermissionsService } from '../../share/permissions.service';
import { SiteDataService, TSiteData } from '../../site/site-data.service';
import { SiteTablePointsService } from '../../site/site-table/site-table-points.service';
import { PointsGenerateService } from '../point-generate/points-generate.service';
import {
  PointAttachmentsService,
  TFilesToUpload,
} from '../point-modal/point-attachments/point-attachments.service';
import { PointsDetailsService } from '../points-details.service';
import { PointsFetchingService } from '../points-fetching.service';
import { PointsService } from '../points.service';
import { EditPointService, TPointFullModal } from './edit-point.service';
import { clearPointModalOpenTimer } from './point-modal-open-monitor';

import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { EKeyboardKeyCode } from 'src/app/core/helpers/keyboard-codes';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { logErrorInSentry } from 'src/app/project/modules/errors/response-error';
import {
  canDeletePoint,
  canEditPoint,
} from 'src/app/project/modules/share/share-utils/share-permissions';
import { GET_SHARE } from 'src/app/project/modules/share/shares.store';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { EWorkspaces } from 'src/app/project/modules/workspace/workspaces.enum';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { TPointDetails } from 'src/app/project/view-models/points-details-response.model';
import { RelatedPointsCacheService } from '../../../components/related-points/related-points-cache.service';
import { NEW_POINT_ID } from '../../../shared/constants/point.const';
import { SiteRedirectService } from '../../auth/site-redirect.service';
import { CustomTableService } from '../../site/site-table/custom-table/custom-table.service';
import { SiteTableService } from '../../site/site-table/site-table.service';
import { checkCustomWorkspaceId } from '../../workspace/workspace';
import { PointModalHeaderService } from '../point-modal-header/point-modal-header.service';
import { PointArrowRedirectService } from '../point-modal/point-arrow-redirect.service';
import { PointHeaderService } from '../point-modal/point-header/point-header.service';
import { selectPointFromState } from '../utils/select-point-from-state';

@Component({
  selector: 'pp-point-full-modal',
  templateUrl: './point-full-modal.component.html',
  styleUrls: ['./point-full-modal.component.scss'],
  standalone: false,
})
export class PointFullModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly paramsChanged$ = new Subject<void>();

  private filesToUpload: TFilesToUpload = this.pointAttachmentsService.getFilesToUpload();
  private fetchingPointId: string | null = null;

  private sync$: Observable<TSync>;
  workspaces$: Observable<TWorkspacesById>;
  workspaces: TWorkspacesById;
  workspace: TWorkspace;
  user$: Observable<TUser>;
  user: TUser;
  private points$: Observable<TPointsByWorkspace>;
  canEditPoint = false;
  canDeletePoint = false;
  detailsFetched = false;
  freshPointFetched = false;

  modal: TPointFullModal = null;
  site: TSiteData = this.siteDataService.getSite();
  activePlan = GET_ACTIVE_PLAN();

  point: TPoint;
  _id: string;
  isDesktopScreen = true;
  commentsEditable: boolean;
  initialTags: string[] = [];
  workspaceId: string;
  isOverview: boolean;
  isTimeline: boolean;
  scrollPosition = 0;
  attachmentUploading = false;
  attachmentUploaded = false;
  nextPointExists = false;
  processingSave = false;
  public isMobile = false;
  public isiPhone = false;
  pointDetails: TPointDetails = null;
  emptyTitle = false;
  NEW_POINT_ID = NEW_POINT_ID;
  relatedPointsFlagEnabled = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private store: Store<{
      workspaces: TWorkspacesById;
      points: TPointsByWorkspace;
      sync: TSync;
      user: TUser;
    }>,
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private editPointService: EditPointService,
    private pointAttachmentsService: PointAttachmentsService,
    private siteDataService: SiteDataService,
    private pointsService: PointsService,
    private siteTablePointsService: SiteTablePointsService,
    private deviceService: DeviceService,
    private sitePointFilterService: SitePointFilterService,
    private activeService: ActiveService,
    private promptService: PromptService,
    private pointsGenerateService: PointsGenerateService,
    private permissionsService: PermissionsService,
    private syncService: SyncService,
    private pointsFetchingService: PointsFetchingService,
    private planPointService: PlanPointService,
    private translationPipe: TranslationPipe,
    private responseErrorService: ResponseErrorService,
    private pointsDetailsService: PointsDetailsService,
    private workspaceService: WorkspaceService,
    private screenService: ScreenService,
    private pointModalHeaderService: PointModalHeaderService,
    private pointHeaderService: PointHeaderService,
    private customTableService: CustomTableService,
    private siteTableService: SiteTableService,
    private siteRedirectService: SiteRedirectService,
    private pointArrowRedirectService: PointArrowRedirectService,
    private featureFlagService: FeatureFlagService,
    private relatedPointsCacheService: RelatedPointsCacheService,
    private ngZone: NgZone,
  ) {
    this.workspaces$ = this.store.pipe(select(EStore.WORKSPACES));
    this.sync$ = this.store.pipe(select(EStore.SYNC));
    this.user$ = this.store.pipe(select(EStore.USER));
    this.points$ = this.store.pipe(select(EStore.POINTS));
  }

  @HostListener('window:keyup', ['$event'])
  keyUpEvent(event: KeyboardEvent): void {
    this.pointArrowRedirectService.tryRedirectPointOnKeyup({
      event,
      attachmentUploading: this.attachmentUploading,
      point: this.point,
      id: this._id,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: true,
    });
  }

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent): void {
    if (
      event.key !== EKeyboardKeyCode.ArrowDown &&
      event.key !== EKeyboardKeyCode.ArrowUp &&
      event.key !== EKeyboardKeyCode.ArrowLeft &&
      event.key !== EKeyboardKeyCode.ArrowRight
    ) {
      return;
    }

    this.pointArrowRedirectService.preventArrowEventIfNeeded(event);
  }

  ngOnInit() {
    this.screenService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktopScreen = isDesktop;
    });

    this.isMobile = this.deviceService.isMobile();
    this.isiPhone = this.deviceService.isiPhone();
    this.relatedPointsFlagEnabled = this.featureFlagService.getFlag(EFeatureFlag.RELATED_POINTS);

    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => this.userHandler(user));
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => this.paramsHandler(params));
    this.workspaces$
      .pipe(takeUntil(this.destroy$))
      .subscribe((workspaces) => this.workspacesHandler(workspaces));
    this.points$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) =>
            selectPointFromState(prev, this.workspaceId, this._id) ===
            selectPointFromState(curr, this.workspaceId, this._id),
        ),
      )
      .subscribe((points) => this.pointsHandler(points));
    this.pointAttachmentsService.fileUploading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUploading) => this.fileUploadingHandler(isUploading));
    this.featureFlagService.flagsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.relatedPointsFlagEnabled = this.featureFlagService.getFlag(EFeatureFlag.RELATED_POINTS);
    });
  }

  ngOnDestroy() {
    clearPointModalOpenTimer();
    this.pointHeaderService.setPointTitle(null);

    if (this._id === NEW_POINT_ID) {
      this.relatedPointsCacheService.clearPendingRelationships(NEW_POINT_ID);
    }

    this.paramsChanged$.next();
    this.destroy$.next();
  }

  userHandler(user: TUser): void {
    this.user = user;
  }

  paramsHandler(params: ParamMap): void {
    this.paramsChanged$.next();

    const openTeardown$ = merge(this.destroy$, this.paramsChanged$);

    const url = this.router.url;
    const paramPointId = params.get('_id');

    this.isOverview = url.search(EWorkspaces.OVERVIEW) !== -1;
    this.isTimeline = url.search(EWorkspaces.TIMELINE) !== -1;

    this._id = paramPointId;

    if (this._id === NEW_POINT_ID) {
      const pointsData = this.pointsService.getPointsData(this.workspaceId);

      if (pointsData) {
        this.modal = this.editPointService.getModal();
        this.workspaceId = this.activeService.getActiveWorkspaceId();
        this.point = this.pointsGenerateService.generateNewPoint(this.workspaceId);

        this.store.dispatch(
          new AddPoint({
            workspaceId: this.workspaceId,
            point: this.point,
          }),
        );

        this.editPointService.showModal(
          this.workspaceId,
          this._id,
          this.isOverview || this.isTimeline,
        );
      } else {
        this.router.navigate(['/site', url.split('/')[2]]);
      }
    } else {
      this.modal = this.editPointService.getModal();
      this.generatePoint(this.pointsService.findPoint(this._id));
      this.pointDetails = null;

      this.prepareWorkspace()
        .pipe(
          takeUntil(openTeardown$),
          tap(() => {
            if (this.isOverview || this.isTimeline) {
              this.fetchPoint();
            } else if (this.siteTableService.getTableLoaded()) {
              this.fetchPoint();
            } else {
              this.siteTableService.tableLoadChange$
                .pipe(
                  filter((tableLoaded) => tableLoaded),
                  take(1),
                  takeUntil(openTeardown$),
                  tap(() => this.fetchPoint()),
                )
                .subscribe();
            }
          }),
        )
        .subscribe();

      if (this.point) {
        this.workspaceId = this.point.workspaceRef.id;
        this.workspacesHandler(this.workspaces);

        this.checkCommentsPermissions();
        this.editPointService.showModal(
          this.workspaceId,
          this._id,
          this.isOverview || this.isTimeline,
        );
      } else {
        this.sync$
          .pipe(
            filter((sync) => sync.basicFetched),
            take(1),
            takeUntil(openTeardown$),
          )
          .subscribe(() => {
            this.pointsFetchingService
              .fetchPoint(this._id)
              .pipe(
                takeUntil(openTeardown$),
                tap((point) => {
                  if (point) {
                    this.workspaceId = point.workspaceRef.id;
                    this.applyFreshPoint(point);
                    this.fetchPointDetailsOnly();
                    this.workspacesHandler(this.workspaces);

                    this.editPointService.showModal(
                      this.workspaceId,
                      this._id,
                      this.isOverview || this.isTimeline,
                    );
                    this.siteTablePointsService.highlightPoint(
                      this.point.sequenceNumber ? this.point.sequenceNumber.toString() : '',
                    );
                  } else {
                    this.onPointLoadError();
                  }
                }),
                catchError(() => {
                  this.handlePointFetchError();

                  return of();
                }),
              )
              .subscribe();
          });
      }
    }
  }

  private onPointLoadError(): void {
    clearPointModalOpenTimer();
    const prompt = this.translationPipe.transform('prompt_point_moved_deleted');

    this.promptService.showWarning(prompt);
    this.editPointService.hideModal(true, true);
  }

  private handlePointFetchError(): void {
    clearPointModalOpenTimer();
    const workspaceId = this.activeService.getActiveWorkspaceId();
    const hasSiteAccess = !!this.workspaceService.getWorkspaces()[workspaceId];

    if (hasSiteAccess) {
      this.onPointLoadError();
    } else {
      this.siteRedirectService.redirectToSites();
    }
  }

  workspacesHandler(workspaces: TWorkspacesById): void {
    if (!workspaces || Object.keys(workspaces).length === 0) {
      return;
    }

    this.workspaces = workspaces;

    if (this.workspaces) {
      this.workspace = this.workspaces[this.workspaceId];

      if (this.workspace) {
        this.checkCommentsPermissions();
      }
    }
  }

  pointsHandler(points: TPointsByWorkspace): void {
    if (this.workspaceId && points[this.workspaceId]) {
      const point = points[this.workspaceId].entities?.find(
        (searchedPoint) => searchedPoint._id === this._id,
      );

      if (!point) {
        this.fetchPoint();
      }

      this.ngZone.runOutsideAngular(() => {
        this.generatePoint((point ? structuredClone(point) : undefined) as TPoint);

        if (this.isTimeline) {
          const timeline = GET_TIMELINE();
          this.nextPointExists = timeline?.getPoints().length > 1;
        } else {
          const table = this.customTableService.getTable();
          this.nextPointExists = table?.getPoints().length > 1;
        }
      });

      if (
        this.workspace?.customFields?.length !== Object.keys(point?.customFieldsMap || {}).length
      ) {
        this.fetchPoint();
      }
    }

    if (this._id === NEW_POINT_ID && this.point) {
      this.checkTitle();
      this.emptyTitle = !this.point.title;
    }
  }

  fetchPoint(): void {
    if (this._id === NEW_POINT_ID || this.fetchingPointId === this._id) {
      return;
    }

    this.fetchingPointId = this._id;

    this.detailsFetched = false;
    this.freshPointFetched = false;

    this.pointsFetchingService
      .fetchPoint(this._id)
      .pipe(
        takeUntil(this.destroy$),
        tap((point) => {
          this.applyFreshPoint(point);
        }),
        catchError((error) => {
          if (error.status === EStatusCode.FORBIDDEN) {
            this.handlePointFetchError();
          }

          return of();
        }),
      )
      .subscribe();

    this.fetchPointDetailsOnly();
  }

  private applyFreshPoint(point: TPoint): void {
    clearPointModalOpenTimer();
    this.generatePoint(this.pointsService.findPoint(this._id) ?? point);
    this.siteTablePointsService.updateTablePoint(point);
    this.freshPointFetched = true;
    this.checkCommentsPermissions();
    this.updateSitePlan();
  }

  private fetchPointDetailsOnly(): void {
    this.pointsDetailsService
      .fetchPointDetails(this._id)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.pointDetails = this.pointsDetailsService.getPointDetails(this._id || null);
          this.detailsFetched = true;
        }),
        catchError(() => of()),
      )
      .subscribe();
  }

  fileUploadingHandler(isUploading: boolean): void {
    this.attachmentUploading = isUploading;
    this.attachmentUploaded = true;
  }

  checkCommentsPermissions(): void {
    if (this.workspaceId) {
      const share = GET_SHARE(this.workspaceId);

      this.canEditPoint = canEditPoint(share.shareOption);
      this.canDeletePoint = canDeletePoint(share.shareOption);

      const commentPermissions = this.permissionsService.getCommentPermissions(this.workspaceId);

      this.commentsEditable = commentPermissions.edit;
    }
  }

  updateScrollPosition(top: number): void {
    this.scrollPosition = top;
  }

  save(): void {
    if (this.attachmentUploading || this.filesToUpload[0]) {
      const promptText = this.translationPipe.transform('prompt_attachments_uploading');

      this.promptService.showWarning(promptText);

      return;
    }

    this.point = this.pointsService.findPoint(NEW_POINT_ID);
    this.checkTitle();

    if (!this.point.title.trim()) {
      this.handlePointTitleEmptyError();

      return;
    }

    this.processingSave = true;

    this.point.tags.push(...this.initialTags);

    this.pointsService
      .createPoint(this.point)
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          const table = this.customTableService.getTable();

          table.addPoint(response);
          const isOverview = checkCustomWorkspaceId() === EWorkspaces.OVERVIEW;

          if (!this.attachmentUploading && !this.activePlan.active) {
            this.hideCreateNewPointModal({ pointCreated: true }).then(() => {
              const promptText = this.translationPipe.transform('prompt_point_created');
              this.promptService.showSuccess(promptText);

              table.reload({ reset: true, isOverview });

              this.store.dispatch(
                new DeletePoint({
                  workspaceId: this.workspaceId,
                  _id: NEW_POINT_ID,
                }),
              );
            });
          }
        }),
        catchError((error) =>
          this.responseErrorService.handleRequestError(error, 'Failed to create point'),
        ),
        finalize(() => {
          this.processingSave = false;
        }),
      )
      .subscribe();
  }

  sendPointTitleEmptyError(titleFieldValue: string, source: string, oldValue?: string): void {
    const startTime = this.syncService.getStartTime();
    const errorTime = new Date();
    const newPoint = this.pointsService.createNewPoint(this.point);
    const timeDifference = errorTime.getTime() - startTime.getTime();

    const errorData = {
      timeDifference,
      pointTitle: oldValue !== undefined ? oldValue : this.point.title,
      inputValue: titleFieldValue,
      point: newPoint,
      source,
    };

    logErrorInSentry({ error: errorData, message: 'Error in point full modal component' }); // here we don't really care about "where" and more about the data
  }

  updateSitePlan(): void {
    this.planPointService.updateFeature(this._id, this.workspaceId, true);
    this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });
  }

  // Point top actions

  prevPoint(): void {
    this.pointModalHeaderService.prevPoint({
      attachmentUploading: this.attachmentUploading,
      point: this.point,
      _id: this._id,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: true,
    });
  }

  nextPoint(): void {
    this.pointModalHeaderService.nextPoint({
      attachmentUploading: this.attachmentUploading,
      point: this.point,
      _id: this._id,
      isTimeline: this.isTimeline,
      isOverview: this.isOverview,
      isFull: true,
    });
  }

  hide({
    clear = true,
    collapse = false,
  }: {
    clear?: boolean;
    collapse?: boolean;
  } = {}): Promise<void> {
    if (this._id === NEW_POINT_ID) {
      this.attachmentUploaded = false;
      this.modalService.showModal(ClosePointModalComponent, { blur: false });
    } else {
      if (!this.attachmentUploading && !this.activePlan.active) {
        if (this.filesToUpload.data.length > 0) {
          for (let index = 0; index < this.filesToUpload.data.length; index += 1) {
            const file = this.filesToUpload.data[index];

            if (!file.uploaded) {
              this.modalService.showModal(UnsavedFilesModalComponent);
              break;
            } else {
              if (collapse) {
                this.location.back();
              }

              return this.editPointService.hideModal(clear);
            }
          }
        } else {
          if (collapse) {
            this.location.back();
          }

          return this.editPointService.hideModal(clear);
        }
      }
    }

    return new Promise(() => {});
  }

  hideCreateNewPointModal({
    clear = true,
    pointCreated = false,
  }: {
    clear?: boolean;
    pointCreated?: boolean;
  } = {}): Promise<void> {
    this.pointAttachmentsService.cancelUploads();
    const workspaceId = this.workspaceId;

    this.store.dispatch(new SetActiveWorkspaceId(workspaceId));
    this.store.dispatch(new SetActivePointId(null));

    if (pointCreated) {
      this.sitePointFilterService.filterPoints();
    }

    return this.editPointService.hideModal(clear);
  }

  private checkTitle(): void {
    if (!this.point.title) {
      const pointTitle = this.pointHeaderService.getPointTitle();

      if (pointTitle !== null) {
        this.point.title = pointTitle;
      }
    }
  }

  private prepareWorkspace(): Observable<TWorkspace> {
    if (this.point && this.workspaces?.[this.point.workspaceRef.id]) {
      return of(null);
    }

    const workspaceId = this.point
      ? this.point.workspaceRef.id
      : this.activeService.getActiveWorkspaceId();

    return this.workspaceService.generateWorkspace(workspaceId);
  }

  private handlePointTitleEmptyError(): void {
    const promptText = this.translationPipe.transform('prompt_title_empty');
    const pointTitle = this.pointHeaderService.getPointTitle();

    let titleFieldValue = 'Title field not found';

    if (pointTitle !== null) {
      titleFieldValue = pointTitle;
    }

    this.sendPointTitleEmptyError(titleFieldValue, 'ERROR');
    this.promptService.showError(promptText);
  }

  generatePoint(point: TPoint): void {
    this.point = this.pointsGenerateService.correctPoint(point);
  }
}
